import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Formik, Form, Field, FieldArray } from "formik";
import { ExerciseFormValues } from "../../types/exercise";
import { exerciseValidationSchema } from "../../validations/exerciseValidations";

import Header from "../../components/Header";
import UploadButton from "../../components/UploadButton";
import BackButton from "../../components/BackButton";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { createExerciseAsync } from "../../redux/slices/exercisesSlice";
import {
  getTargetMuscles,
  uploadFile,
  TargetMuscle,
  getWorkoutPrograms,
  WorkoutProgram,
  getWorkoutCategories,
  WorkoutCategory,
} from "../../services/exerciseService";
import { Equipment, getAllEquipments } from "../../services/equipmentService";
import {
  HowToPerform,
  getHowToPerforms,
} from "../../services/howToPerformService";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import ReactQuill from "react-quill";
import Select from "react-select";

const AddExercise: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [targetMuscles, setTargetMuscles] = useState<TargetMuscle[]>([]);
  const [workoutPrograms, setWorkoutPrograms] = useState<WorkoutProgram[]>([]);
  const [workoutCategories, setWorkoutCategories] = useState<WorkoutCategory[]>(
    [],
  );
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [howToPerforms, setHowToPerforms] = useState<HowToPerform[]>([]);
  const [uploadedImageNames, setUploadedImageNames] = useState<
    (string | null)[]
  >([null, null, null]);
  // const [uploadedVideoNames, setUploadedVideoNames] = useState<(string | null)[]>([null, null]);

  useEffect(() => {
    const fetchTargetMuscles = async () => {
      try {
        const muscles = await getTargetMuscles();
        setTargetMuscles(muscles);
      } catch (error) {
        console.error("Error fetching target muscles:", error);
      }
    };

    fetchTargetMuscles();
  }, []);

  useEffect(() => {
    const fetchWorkoutPrograms = async () => {
      try {
        const programs = await getWorkoutPrograms();
        setWorkoutPrograms(programs);
      } catch (error) {
        console.error("Error fetching workout programs:", error);
      }
    };

    fetchWorkoutPrograms();
  }, []);

  useEffect(() => {
    const fetchWorkoutCategories = async () => {
      try {
        const categories = await getWorkoutCategories();
        setWorkoutCategories(categories);
      } catch (error) {
        console.error("Error fetching workout categories:", error);
      }
    };

    fetchWorkoutCategories();
  }, []);

  useEffect(() => {
    const fetchAllEquipments = async () => {
      try {
        const equipmentList = await getAllEquipments();
        setEquipments(equipmentList);
      } catch (error) {
        console.error("Error fetching equipment:", error);
      }
    };

    fetchAllEquipments();
  }, []);

  useEffect(() => {
    const fetchAllHowToPerforms = async () => {
      try {
        const response = await getHowToPerforms({ limit: 1000 });
        setHowToPerforms(response.data);
      } catch (error) {
        console.error("Error fetching how-to-perform tutorials:", error);
      }
    };

    fetchAllHowToPerforms();
  }, []);

  const handleImageUpload = async (
    file: File | null,
    index: number,
    setFieldValue: (field: string, value: any) => void,
    values: ExerciseFormValues,
  ): Promise<string | null> => {
    if (!file) return null;

    try {
      const response = await uploadFile(file);
      const { url } = response;

      if (url) {
        const newImages = [...values.images];
        newImages[index] = url;
        setFieldValue("images", newImages);
        return url;
      } else {
        setErrorMessage("Failed to upload image");
        return null;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setErrorMessage("Failed to upload image");
      return null;
    }
  };

  const handleVideoUpload = async (
    file: File | null,
    index: number,
    setFieldValue: (field: string, value: any) => void,
    validateForm: () => void,
    // values: ExerciseFormValues
  ): Promise<string | null> => {
    if (!file) return null;

    try {
      console.log(`Uploading video at index ${index}...`);
      setFieldValue(`uploadInProgress.video${index}`, true);
      setFieldValue("disableValidation", true);

      const response = await uploadFile(file);

      if (!response || !response.url || !response.thumbnail) {
        throw new Error("Invalid response from upload service");
      }

      const { url, thumbnail } = response;

      setFieldValue(`videos[${index}]`, { url, thumbnail });

      console.log(`Video ${index + 1} uploaded successfully:`, {
        url,
        thumbnail,
      });

      setFieldValue(`uploadInProgress.video${index}`, false);
      setFieldValue("disableValidation", false);

      validateForm();

      return url;
    } catch (error) {
      console.error("Error uploading video:", error);
      setFieldValue(`uploadInProgress.video${index}`, false);
      setFieldValue("disableValidation", false);
      setErrorMessage("Failed to upload video. Please try again.");
      return null;
    }
  };

  const handleImageRemove = (
    index: number,
    setFieldValue: (field: string, value: any) => void,
    values: ExerciseFormValues,
  ) => {
    const newImages = [...values.images];
    newImages[index] = null;
    setFieldValue("images", newImages);
    console.log("newImages", newImages);

    const newImageNames = [...uploadedImageNames];
    newImageNames[index] = null;
    setUploadedImageNames(newImageNames);
  };

  const handleVideoRemove = (
    index: number,
    setFieldValue: (field: string, value: any) => void,
    setFieldTouched: (field: string, isTouched?: boolean) => void,
    validateForm: () => void,
    // values: ExerciseFormValues
  ) => {
    if (index === 0) {
      setFieldValue("video1", null);
      setFieldTouched("video1", true);
    } else if (index === 1) {
      setFieldValue("video2", null);
      setFieldTouched("video2", true);
    }

    validateForm();
  };

  const initialValues: ExerciseFormValues = {
    name: "",
    workoutCategory: [],
    workoutProgram: [],
    targetMuscles: "",
    secondaryTargetMuscles: "",
    gender: [],
    fitnessLevel: [],
    mood: [],
    fitnessGoal: [],
    priority: "",
    description: "",
    images: [null],
    videos: [null, null],
    uploadInProgress: { video0: false, video1: false },
    disableValidation: false,
    video1: null,
    video2: null,
    workingSets: [{ set: 1, kg: "", reps: "", kilometers: "", minutes: "" }],
    equipmentIds: [],
    howToPerformIds: [],
    exerciseType: "Clear",
  };

  const handleMultiSelect = (
    e: React.ChangeEvent<HTMLSelectElement>,
    field: string,
    setFieldValue: (field: string, value: any) => void,
    currentValues: string[],
  ) => {
    const value = e.target.value;

    if (value === "") {
      return;
    }

    if (!currentValues.includes(value)) {
      setFieldValue(field, [...currentValues, value]);
    }
  };

  const handleSubmit = async (values: ExerciseFormValues) => {
    try {
      const exerciseData = {
        name: values.name,
        description: values.description,
        imageUrls: values.images.filter((url) => url !== null) as string[],
        videoUrls: values.videos
          .filter((video) => video && video.url !== null)
          .map((video) => ({
            url: video!.url,
            thumbnail: video!.thumbnail,
          })),
        mood: values.mood,
        gender: values.gender,
        exerciseType: values.exerciseType,
        workingSets: values.workingSets.map((set) => ({
          set: set.set,
          kg: parseFloat(set.kg || "0"),
          reps: parseInt(set.reps || "0", 10),
          time: parseInt(set.time || "0", 10),
          meters: parseInt(set.meters || "0", 10),
          kilometers: parseInt(set.kilometers || "0", 10),
          minutes: parseInt(set.minutes || "0", 10),
        })),
        fitnessLevel: values.fitnessLevel,
        fitnessGoal: values.fitnessGoal,
        priority: values.priority,
        primaryTargetMuscles: values.targetMuscles
          ? [parseInt(values.targetMuscles)]
          : [],
        secondaryTargetMuscles: values.secondaryTargetMuscles
          ? [parseInt(values.secondaryTargetMuscles)]
          : [],
        programIds: values.workoutProgram.map((id) => parseInt(id)),
        categoryIds: values.workoutCategory.map((id) => parseInt(id)),
        equipmentIds: values.equipmentIds.map((id) => parseInt(id)),
        howToPerformIds: values.howToPerformIds.map((id) => parseInt(id)),
      };

      console.log("add", exerciseData);

      await dispatch(createExerciseAsync(exerciseData)).unwrap();
      setSuccessMessage("Exercise added successfully!");
      setTimeout(() => {
        navigate("/exercises");
      }, 2000);
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to create exercise");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Add Exercise" />

      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}

      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}

      <div className="flex items-center justify-between mt-4 border-b pb-4">
        <BackButton onClick={() => navigate("/exercises")} />
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={exerciseValidationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={true}
        validate={(values) => {
          if (values.disableValidation) {
            return {};
          }

          return undefined;
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          validateForm,
          setFieldTouched,
        }) => {
          console.log("a", errors);
          return (
            <Form className="mt-6 bg-white p-6 rounded-lg shadow-md border">
              <div className="grid grid-cols-3 gap-6">
                {/* First Column */}
                <div className="col-span-3 sm:col-span-1">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Exercise Name
                    </label>
                    <Field
                      name="name"
                      type="text"
                      maxLength={100}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter exercise name"
                    />
                    {errors.name && touched.name && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.name}
                      </div>
                    )}
                  </div>

                  <div className="col-span-3 flex gap-4 mt-4">
                    <div className="flex flex-col items-center">
                      <UploadButton
                        key={0}
                        index={0}
                        label="Upload Image"
                        onUpload={(file, index) =>
                          handleImageUpload(file, index, setFieldValue, values)
                        }
                        onRemove={(index) =>
                          handleImageRemove(index, setFieldValue, values)
                        }
                        accept="image/*"
                      />
                    </div>
                    <div className="flex flex-col items-center">
                      <UploadButton
                        key={0}
                        index={0}
                        label="Upload Video 1"
                        onUpload={async (file) => {
                          const videoUrl = await handleVideoUpload(
                            file,
                            0,
                            setFieldValue,
                            validateForm,
                            // values
                          );
                          return videoUrl;
                        }}
                        onRemove={() =>
                          handleVideoRemove(
                            0,
                            setFieldValue,
                            setFieldTouched,
                            validateForm,
                            // values
                          )
                        }
                        accept="video/*"
                      />
                    </div>
                    <div className="flex flex-col items-center">
                      <UploadButton
                        key={1}
                        index={1}
                        label="Upload Video 2"
                        onUpload={async (file) => {
                          const videoUrl = await handleVideoUpload(
                            file,
                            1,
                            setFieldValue,
                            validateForm,
                            // values
                          );
                          return videoUrl;
                        }}
                        onRemove={() =>
                          handleVideoRemove(
                            1,
                            setFieldValue,
                            setFieldTouched,
                            validateForm,
                            // values
                          )
                        }
                        accept="video/*"
                      />
                    </div>
                  </div>
                  <div className="flex">
                    {errors.images && touched.images && (
                      <div className="text-red-500 text-xs mt-2">
                        {errors.images[0]}
                      </div>
                    )}
                    {errors.video1 && touched.video1 && (
                      <div className="text-red-500 text-xs mt-2 ml-2">
                        {errors.video1}
                      </div>
                    )}
                    {errors.video2 && touched.video2 && (
                      <div className="text-red-500 text-xs mt-2 ml-2">
                        {errors.video2}
                      </div>
                    )}
                  </div>
                  <div className="col-span-3 mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Instructions
                    </label>
                    <ReactQuill
                      value={values.description}
                      onChange={(content) =>
                        setFieldValue("description", content)
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm"
                    />
                    {errors.description && touched.description && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.description}
                      </div>
                    )}
                  </div>

                  <div className="col-span-3 mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Exercise Type
                    </label>
                    <Field
                      as="select"
                      name="exerciseType"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="Clear">Clear</option>
                      <option value="time_based">Time-based</option>
                      <option value="weight_and_reps_based">
                        Weight and reps-based
                      </option>
                      <option value="distance_based">Distance-based</option>
                      <option value="cardio">Cardio</option>
                      <option value="bodyweight">Bodyweight</option>
                    </Field>
                    {errors.exerciseType && touched.exerciseType && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.exerciseType}
                      </div>
                    )}
                  </div>

                  <FieldArray name="workingSets">
                    {({ push, remove }) => (
                      <div className="mt-4 bg-white p-3 rounded-lg shadow-md border">
                        <h3 className="text-sm font-semibold text-gray-900 mb-2">
                          Working Sets
                        </h3>

                        {values.exerciseType === "Clear" ? (
                          <div className="text-gray-500 text-sm">
                            Select an exercise type to display working sets.
                          </div>
                        ) : (
                          <>
                            <div className="grid gap-2 mb-2">
                              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 text-xs font-semibold">
                                <span className="text-gray-700">Set</span>
                                {values.exerciseType === "time_based" && (
                                  <span className="text-gray-700">Time</span>
                                )}
                                {values.exerciseType ===
                                  "weight_and_reps_based" && (
                                  <>
                                    <span className="text-gray-700">Kg</span>
                                    <span className="text-gray-700">Reps</span>
                                  </>
                                )}
                                {values.exerciseType === "distance_based" && (
                                  <span className="text-gray-700">Meters</span>
                                )}
                                {values.exerciseType === "cardio" && (
                                  <>
                                    <span className="text-gray-700">Km</span>
                                    <span className="text-gray-700">Min</span>
                                  </>
                                )}
                                {values.exerciseType === "bodyweight" && (
                                  <span className="text-gray-700">Reps</span>
                                )}
                              </div>

                              {values.workingSets.map((_, index) => (
                                <div
                                  key={index}
                                  className="grid grid-cols-3 sm:grid-cols-4 gap-2 items-center"
                                >
                                  <div className="text-center">{index + 1}</div>
                                  {values.exerciseType === "time_based" && (
                                    <Field
                                      name={`workingSets.${index}.time`}
                                      type="number"
                                      placeholder="Time"
                                      required
                                      min="0"
                                      className="w-full border border-gray-300 rounded px-1 py-1 text-xs"
                                    />
                                  )}
                                  {values.exerciseType ===
                                    "weight_and_reps_based" && (
                                    <>
                                      <Field
                                        name={`workingSets.${index}.kg`}
                                        type="number"
                                        placeholder="Kg"
                                        required
                                        min="0"
                                        max={400}
                                        className="w-full border border-gray-300 rounded px-1 py-1 text-xs"
                                      />
                                      <Field
                                        name={`workingSets.${index}.reps`}
                                        type="number"
                                        required
                                        placeholder="Reps"
                                        min="1"
                                        max={150}
                                        className="w-full border border-gray-300 rounded px-1 py-1 text-xs"
                                      />
                                    </>
                                  )}

                                  {values.exerciseType === "cardio" && (
                                    <>
                                      <Field
                                        name={`workingSets.${index}.kilometers`}
                                        type="decimal"
                                        placeholder="Kilometers"
                                        required
                                        min="0.1"
                                        max={100000}
                                        className="w-full border border-gray-300 rounded px-1 py-1 text-xs"
                                      />
                                      <Field
                                        name={`workingSets.${index}.minutes`}
                                        type="number"
                                        required
                                        placeholder="Minutes"
                                        min="1"
                                        max={1000}
                                        className="w-full border border-gray-300 rounded px-1 py-1 text-xs"
                                      />
                                    </>
                                  )}
                                  {values.exerciseType === "bodyweight" && (
                                    <Field
                                      name={`workingSets.${index}.reps`}
                                      type="number"
                                      required
                                      placeholder="Reps"
                                      min="1"
                                      max={150}
                                      className="w-full border border-gray-300 rounded px-1 py-1 text-xs"
                                    />
                                  )}
                                  {values.workingSets.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-500 text-xs hover:underline"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                            {errors.workingSets && touched.workingSets && (
                              <div className="text-red-500 text-xs mt-2">
                                {Array.isArray(errors.workingSets)
                                  ? errors.workingSets.join(", ")
                                  : errors.workingSets}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() =>
                                push({
                                  set: values.workingSets.length + 1,
                                  ...(values.exerciseType === "time_based" && {
                                    time: "",
                                  }),
                                  ...(values.exerciseType ===
                                    "weight_and_reps_based" && {
                                    kg: "",
                                    reps: "",
                                  }),
                                  ...(values.exerciseType ===
                                    "distance_based" && { meters: "" }),
                                  ...(values.exerciseType === "bodyweight" && {
                                    reps: "",
                                  }),
                                })
                              }
                              className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                            >
                              Add Set
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </FieldArray>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Equipment
                    </label>
                    <Field
                      as="select"
                      name="equipmentIds"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleMultiSelect(
                          e,
                          "equipmentIds",
                          setFieldValue,
                          values.equipmentIds,
                        )
                      }
                    >
                      <option value="">Clear</option>
                      {equipments
                        .filter(
                          (equipment) =>
                            !values.equipmentIds.includes(
                              equipment.id.toString(),
                            ),
                        )
                        .map((equipment) => (
                          <option key={equipment.id} value={equipment.id}>
                            {equipment.name}
                          </option>
                        ))}
                    </Field>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {values.equipmentIds.map((eqId) => {
                        const equipment = equipments.find(
                          (eq) => eq.id.toString() === eqId,
                        );
                        return equipment ? (
                          <div
                            key={eqId}
                            className="flex items-center bg-gray-100 px-2 py-1 rounded"
                          >
                            <span className="text-sm">{equipment.name}</span>
                            <button
                              type="button"
                              className="ml-2 text-gray-500 hover:text-red-500 z-10"
                              onClick={() => {
                                setFieldValue(
                                  "equipmentIds",
                                  values.equipmentIds.filter(
                                    (id) => id !== eqId,
                                  ),
                                );
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Trainer Tutorials
                    </label>
                    <Select
                      isMulti
                      classNamePrefix="react-select"
                      className="mt-1"
                      placeholder="Search tutorials by name..."
                      options={howToPerforms.map((htp) => ({
                        value: htp.id.toString(),
                        label: htp.name,
                      }))}
                      value={values.howToPerformIds
                        .map((id) => {
                          const htp = howToPerforms.find(
                            (h) => h.id.toString() === id,
                          );
                          return htp
                            ? { value: id, label: htp.name }
                            : null;
                        })
                        .filter(
                          (option): option is { value: string; label: string } =>
                            option !== null,
                        )}
                      onChange={(selected) =>
                        setFieldValue(
                          "howToPerformIds",
                          selected ? selected.map((option) => option.value) : [],
                        )
                      }
                    />
                  </div>
                </div>

                <div className="col-span-3 sm:col-span-1">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Workout Category
                    </label>
                    <Field
                      as="select"
                      name="workoutCategory"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleMultiSelect(
                          e,
                          "workoutCategory",
                          setFieldValue,
                          values.workoutCategory,
                        )
                      }
                    >
                      <option value="">Clear</option>
                      {workoutCategories
                        .filter(
                          (category) =>
                            !values.workoutCategory.includes(
                              category.id.toString(),
                            ),
                        )
                        .map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.title}
                          </option>
                        ))}
                    </Field>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {values.workoutCategory.map((catId) => {
                        const category = workoutCategories.find(
                          (cat) => cat.id.toString() === catId,
                        );
                        return category ? (
                          <div
                            key={catId}
                            className="flex items-center bg-gray-100 px-2 py-1 rounded"
                          >
                            <span className="text-sm">{category.title}</span>
                            <button
                              type="button"
                              className="ml-2 text-gray-500 hover:text-red-500"
                              onClick={() => {
                                setFieldValue(
                                  "workoutCategory",
                                  values.workoutCategory.filter(
                                    (id) => id !== catId,
                                  ),
                                );
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Programs
                    </label>
                    <Field
                      as="select"
                      name="workoutProgram"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleMultiSelect(
                          e,
                          "workoutProgram",
                          setFieldValue,
                          values.workoutProgram,
                        )
                      }
                    >
                      <option value="">Clear</option>
                      {workoutPrograms
                        .filter(
                          (program) =>
                            !values.workoutProgram.includes(
                              program.id.toString(),
                            ),
                        )
                        .map((program) => (
                          <option key={program.id} value={program.id}>
                            {program.title}
                          </option>
                        ))}
                    </Field>
                    {/* Display selected programs */}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {values.workoutProgram.map((progId) => {
                        const program = workoutPrograms.find(
                          (prog) => prog.id.toString() === progId,
                        );
                        return program ? (
                          <div
                            key={progId}
                            className="flex items-center bg-gray-100 px-2 py-1 rounded"
                          >
                            <span className="text-sm">{program.title}</span>
                            <button
                              type="button"
                              className="ml-2 text-gray-500 hover:text-red-500"
                              onClick={() => {
                                setFieldValue(
                                  "workoutProgram",
                                  values.workoutProgram.filter(
                                    (id) => id !== progId,
                                  ),
                                );
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Primary Target Muscles
                    </label>
                    <Field
                      as="select"
                      name="targetMuscles"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">Clear</option>
                      {targetMuscles.map((muscle) => (
                        <option key={muscle.id} value={muscle.id}>
                          {muscle.name}
                        </option>
                      ))}
                    </Field>
                    {errors.targetMuscles && touched.targetMuscles && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.targetMuscles}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Secondary Target Muscles
                    </label>
                    <Field
                      as="select"
                      name="secondaryTargetMuscles"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">Clear</option>
                      {targetMuscles
                        .filter(
                          (muscle) =>
                            muscle.id.toString() !== values.targetMuscles,
                        )
                        .map((muscle) => (
                          <option key={muscle.id} value={muscle.id}>
                            {muscle.name}
                          </option>
                        ))}
                    </Field>

                    {errors.secondaryTargetMuscles &&
                      touched.secondaryTargetMuscles && (
                        <div className="text-red-500 text-xs mt-1">
                          {errors.secondaryTargetMuscles}
                        </div>
                      )}
                  </div>
                </div>

                {/* Third Column */}
                <div className="col-span-3 sm:col-span-1">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Gender
                    </label>
                    <Field
                      as="select"
                      name="gender"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleMultiSelect(
                          e,
                          "gender",
                          setFieldValue,
                          values.gender,
                        )
                      }
                    >
                      <option value="">Clear</option>
                      {["male", "female"]
                        .filter((g) => !values.gender.includes(g))
                        .map((gender) => (
                          <option key={gender} value={gender}>
                            {gender === "male" ? "Male" : "Female"}
                          </option>
                        ))}
                    </Field>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {values.gender.map((gen) => (
                        <div
                          key={gen}
                          className="flex items-center bg-gray-100 px-2 py-1 rounded"
                        >
                          <span className="text-sm">
                            {gen === "male" ? "Male" : "Female"}
                          </span>
                          <button
                            type="button"
                            className="ml-2 text-gray-500 hover:text-red-500"
                            onClick={() =>
                              setFieldValue(
                                "gender",
                                values.gender.filter((item) => item !== gen),
                              )
                            }
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    {errors.gender && touched.gender && (
                      <div className="text-red-500 text-xs">
                        {errors.gender}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Fitness Level
                    </label>
                    <Field
                      as="select"
                      name="fitnessLevel"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleMultiSelect(
                          e,
                          "fitnessLevel",
                          setFieldValue,
                          values.fitnessLevel,
                        )
                      }
                    >
                      <option value="">Clear</option>
                      {["beginner", "intermediate", "advanced"]
                        .filter((level) => !values.fitnessLevel.includes(level))
                        .map((level) => (
                          <option key={level} value={level}>
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </option>
                        ))}
                    </Field>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {values.fitnessLevel.map((level) => (
                        <div
                          key={level}
                          className="flex items-center bg-gray-100 px-2 py-1 rounded"
                        >
                          <span className="text-sm">
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </span>
                          <button
                            type="button"
                            className="ml-2 text-gray-500 hover:text-red-500"
                            onClick={() =>
                              setFieldValue(
                                "fitnessLevel",
                                values.fitnessLevel.filter(
                                  (item) => item !== level,
                                ),
                              )
                            }
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>

                    {errors.fitnessLevel && touched.fitnessLevel && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.fitnessLevel}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Exercise Priority
                    </label>
                    <Field
                      as="select"
                      name="priority"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">Clear</option>
                      <option value="high">1 (High)</option>
                      <option value="medium">2 (Middle)</option>
                      <option value="low">3 (Low)</option>
                    </Field>
                    {errors.priority && touched.priority && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.priority}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Fitness Mood
                    </label>
                    <Field
                      as="select"
                      name="mood"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleMultiSelect(e, "mood", setFieldValue, values.mood)
                      }
                    >
                      <option value="">Clear</option>
                      {["energetic", "normal", "tired"]
                        .filter((mood) => !values.mood.includes(mood))
                        .map((mood) => (
                          <option key={mood} value={mood}>
                            {mood.charAt(0).toUpperCase() + mood.slice(1)}
                          </option>
                        ))}
                    </Field>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {values.mood.map((m) => (
                        <div
                          key={m}
                          className="flex items-center bg-gray-100 px-2 py-1 rounded"
                        >
                          <span className="text-sm">
                            {m.charAt(0).toUpperCase() + m.slice(1)}
                          </span>
                          <button
                            type="button"
                            className="ml-2 text-gray-500 hover:text-red-500"
                            onClick={() =>
                              setFieldValue(
                                "mood",
                                values.mood.filter((item) => item !== m),
                              )
                            }
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    {errors.mood && touched.mood && (
                      <div className="text-red-500 text-xs">{errors.mood}</div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Fitness Goal
                    </label>
                    <Field
                      as="select"
                      name="fitnessGoal"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleMultiSelect(
                          e,
                          "fitnessGoal",
                          setFieldValue,
                          values.fitnessGoal,
                        )
                      }
                    >
                      <option value="">Clear</option>
                      {["lose-weight", "build-muscles"]
                        .filter((goal) => !values.fitnessGoal.includes(goal))
                        .map((goal) => (
                          <option key={goal} value={goal}>
                            {goal === "lose-weight"
                              ? "Lose Weight"
                              : "Build Muscles"}
                          </option>
                        ))}
                    </Field>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {values.fitnessGoal.map((goal) => (
                        <div
                          key={goal}
                          className="flex items-center bg-gray-100 px-2 py-1 rounded"
                        >
                          <span className="text-sm">
                            {goal === "lose-weight"
                              ? "Lose Weight"
                              : "Build Muscles"}
                          </span>
                          <button
                            type="button"
                            className="ml-2 text-gray-500 hover:text-red-500"
                            onClick={() =>
                              setFieldValue(
                                "fitnessGoal",
                                values.fitnessGoal.filter(
                                  (item) => item !== goal,
                                ),
                              )
                            }
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    {errors.fitnessGoal && touched.fitnessGoal && (
                      <div className="text-red-500 text-xs">
                        {errors.fitnessGoal}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="mt-6 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => navigate("/exercises")}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition"
                >
                  Add Exercise
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AddExercise;
